import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { ChipProps } from '@mui/material';
import { listPayments } from '../../api/payments';
import { PaymentStatus } from '../../types/payment';
import { EmptyState } from '../../components/EmptyState';

const STATUS_COLOR: Record<PaymentStatus, ChipProps['color']> = {
  PENDING: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
  REVERSED: 'warning',
};

export const RecentPaymentsCard = () => {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['dashboard', 'recent-payments'],
    queryFn: () => listPayments({ page: 1, pageSize: 5 }),
  });

  const payments = query.data?.data ?? [];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Recent payments</Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/payments')}
        >
          View all
        </Typography>
      </Stack>
      {payments.length === 0 ? (
        <EmptyState loading={query.isLoading} message="No payments yet" />
      ) : (
        <List dense disablePadding>
          {payments.map((payment) => (
            <ListItemButton key={payment.id} onClick={() => navigate('/payments')} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText
                // Long names/emails shorten with "…" instead of widening the card (no sideways scroll on a phone).
                sx={{ minWidth: 0 }}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
                primary={`${payment.student.firstName} ${payment.student.lastName}`}
                secondary={`${payment.paymentNumber} · ${payment.currency.symbol ?? payment.currency.code}${payment.amount}`}
              />
              <Box>
                <Chip size="small" label={payment.status} color={STATUS_COLOR[payment.status]} />
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
